import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, coverUri, resolveAudioUrl, PERFORMANCE_VOICE } from "../lib/api";
import { fmtClock } from "../lib/format";
import { useAuth } from "../auth";
import { useAudio } from "../lib/useAudio";
import { MarkersPanel } from "../components/Markers";
import { useBreakpoint } from "../lib/responsive";
import { Spinner, EmptyState, StatusPill, ProgressBar, Chip } from "../components/ui";
import { Icon } from "../components/icons";
import { SeekBar } from "../components/SeekBar";
import { DocumentList, docKind } from "../components/Documents";
import { Piano } from "../components/Piano";
import { LyricsAssistant } from "../components/Assistant";

const STEPS = [0, 25, 50, 75, 100];

export default function Song() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const autoplay = sp.get("autoplay");
  const navigate = useNavigate();
  const fromCtx = useLocation().state?.from;
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(fromCtx?.path || "/repertorio"));
  const qc = useQueryClient();
  const { user } = useAuth();
  const { isWide } = useBreakpoint();

  const q = useQuery({ queryKey: ["song", id], queryFn: () => api(`/songs/${id}`) });
  const song = q.data;

  const [tab, setTab] = useState("letra");
  const [current, setCurrent] = useState(null);
  const { status, replace, play, pause, seekTo } = useAudio();
  const autoplayed = useRef(false);

  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);
  const [looping, setLooping] = useState(false);
  const loop = loopA !== null && loopB !== null && loopB > loopA ? { a: loopA, b: loopB } : null;
  const loopRef = useRef(null);
  loopRef.current = looping && loop ? loop : null;

  const progress = useMutation({
    mutationFn: (percent) => api(`/songs/${id}/progress`, { method: "PUT", body: JSON.stringify({ percent }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["song", id] });
      qc.invalidateQueries({ queryKey: ["songs"] });
      qc.invalidateQueries({ queryKey: ["home"] });
    },
  });

  useEffect(() => {
    const l = loopRef.current;
    if (!l) return;
    if (status.currentTime >= l.b || (status.didJustFinish && l.b >= (status.duration || 0) - 0.5)) {
      seekTo(l.a);
      if (!status.playing) play();
    }
  }, [status.currentTime, status.didJustFinish, status.playing, status.duration, play, seekTo]);

  const playPart = (part) => {
    if (!part?.audio_url) return;
    if (current === part.voice) {
      if (status.playing) pause();
      else play();
      return;
    }
    replace(resolveAudioUrl(part.audio_url));
    play();
    setCurrent(part.voice);
  };

  const myPart = song?.parts?.find((p) => p.voice === user?.voice);
  const performance = song?.parts?.find((p) => p.voice === PERFORMANCE_VOICE && p.audio_url);
  const others = song?.parts?.filter((p) => p.voice !== user?.voice && p.voice !== PERFORMANCE_VOICE) || [];
  const scores = song?.documents?.filter((d) => docKind(d) === "partitura") || [];
  const lyricsDocs = song?.documents?.filter((d) => docKind(d) === "letra") || [];
  const otherDocs = song?.documents?.filter((d) => docKind(d) === "outro") || [];

  useEffect(() => {
    if (autoplay === "1" && myPart && !autoplayed.current) {
      autoplayed.current = true;
      playPart(myPart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, myPart]);

  const myPlaying = current === myPart?.voice && status.playing;
  const perfPlaying = current === PERFORMANCE_VOICE && status.playing;
  const cover = coverUri(song?.cover_url);
  const setLoopRange = (a, b) => {
    setLoopA(a);
    setLoopB(b);
    setLooping(true);
    seekTo(a);
    play();
  };
  const clearLoop = () => {
    setLooping(false);
    setLoopA(null);
    setLoopB(null);
  };

  return (
    <div style={{ position: "relative", minHeight: "100%" }}>
      {cover ? (
        <>
          <img src={cover} alt="" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 700, width: "100%", objectFit: "cover", filter: "blur(60px)", opacity: 0.5 }} />
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 700, background: "linear-gradient(to bottom, var(--scrim-soft) 0%, var(--scrim) 45%, var(--surface) 80%)" }} />
        </>
      ) : null}

      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 12px 8px" }}>
          <button className="icon-btn" data-testid="song-back-button" aria-label="Voltar" onClick={goBack}>
            <Icon name="chevron-down" size={24} />
          </button>
          <div style={{ flex: 1, textAlign: "center", color: "var(--on-surface-3)", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {fromCtx?.path ? (
              <button onClick={goBack} className="chip" data-testid="song-back-context" style={{ maxWidth: "100%" }}>
                <Icon name="chevron-back" size={13} /> VOLTAR · {(fromCtx.label || "").toUpperCase()}
              </button>
            ) : song?.project?.toUpperCase()}
          </div>
          <div style={{ width: 44 }} />
        </div>

        {q.isLoading ? (
          <Spinner />
        ) : !song ? (
          <EmptyState icon="alert-circle-outline" text="Música não encontrada." />
        ) : (
          <div className="ulc-fade" style={{ maxWidth: isWide ? 1200 : 560, width: "100%", margin: "0 auto", padding: "0 24px 48px" }} data-testid="song-layout">
            <div style={isWide ? { display: "flex", alignItems: "flex-start", gap: 32, paddingTop: 8 } : { display: "flex", flexDirection: "column", gap: 24 }}>
              {/* LEFT: cover + player */}
              <div style={isWide ? { width: 440, flexShrink: 0, display: "flex", flexDirection: "column", gap: 24 } : { display: "flex", flexDirection: "column", gap: 24 }}>
                {cover ? (
                  <img src={cover} alt="" style={{ width: "100%", maxWidth: 320, aspectRatio: "1", alignSelf: "center", borderRadius: "var(--r-lg)", objectFit: "cover", background: "var(--surface-3)" }} />
                ) : (
                  <div style={{ width: "100%", maxWidth: 320, aspectRatio: "1", alignSelf: "center", borderRadius: "var(--r-lg)", background: "var(--surface-3)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="musical-notes" size={48} color="var(--muted)" />
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div className="t-h1" data-testid="song-title" style={{ fontFamily: "var(--f-medium)", fontWeight: 500, fontSize: 30 }}>{song.title}</div>
                  <div data-testid="song-artist" style={{ color: "var(--on-surface-3)", fontSize: 16 }}>
                    {song.artist}{song.duration ? ` · ${song.duration}` : ""}
                  </div>
                </div>

                {myPart ? (
                  <button data-testid="my-voice-play-button" onClick={() => playPart(myPart)} style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--brand-primary)", borderRadius: 999, padding: 8, paddingRight: 24, border: "none", cursor: "pointer", width: "100%" }}>
                    <span style={{ width: 60, height: 60, borderRadius: 999, background: "var(--on-brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name={myPlaying ? "pause" : "play"} size={26} color="var(--brand-primary)" />
                    </span>
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={{ display: "block", color: "var(--on-brand)", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 2.5, opacity: 0.8 }}>A MINHA VOZ</span>
                      <span style={{ display: "block", color: "var(--on-brand)", fontFamily: "var(--f-medium)", fontSize: 20 }}>{myPart.label}</span>
                    </span>
                    {myPlaying ? <Icon name="volume-high" size={20} color="var(--on-brand)" /> : null}
                  </button>
                ) : (
                  <div style={{ padding: 16, background: "var(--surface-2)", borderRadius: "var(--r-md)", color: "var(--on-surface-3)" }}>Sem faixa para a sua voz.</div>
                )}

                {performance ? (
                  <button data-testid="performance-play-button" onClick={() => playPart(performance)} style={{ display: "flex", alignItems: "center", gap: 12, background: current === PERFORMANCE_VOICE ? "var(--brand-tertiary)" : "var(--surface-2)", borderRadius: 999, padding: 8, paddingRight: 16, border: `1px solid ${current === PERFORMANCE_VOICE ? "var(--brand-primary)" : "var(--border-strong)"}`, cursor: "pointer", width: "100%" }}>
                    <span style={{ width: 48, height: 48, borderRadius: 999, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name={perfPlaying ? "pause" : "play"} size={22} color="var(--brand-primary)" />
                    </span>
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={{ display: "block", color: "var(--brand-primary)", fontFamily: "var(--f-medium)", fontSize: 10, letterSpacing: 2 }}>VERSÃO DE ESPETÁCULO</span>
                      <span style={{ display: "block", color: "var(--on-surface)", fontFamily: "var(--f-medium)", fontSize: 15 }}>Música completa, como em concerto</span>
                    </span>
                    <Icon name={perfPlaying ? "volume-high" : "star"} size={18} color="var(--brand-primary)" />
                  </button>
                ) : null}

                {current ? (
                  <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }} data-testid="song-player">
                    <div className="row-between">
                      <span style={{ color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 10, letterSpacing: 2 }}>A OUVIR · {current.toUpperCase()}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button className="icon-btn" data-testid="player-back-10" onClick={() => seekTo(Math.max(0, status.currentTime - 10))}>
                          <Icon name="play-back" size={18} />
                        </button>
                        <button className="icon-btn" data-testid="player-toggle" onClick={() => (status.playing ? pause() : play())} style={{ background: "var(--brand-primary)" }}>
                          <Icon name={status.playing ? "pause" : "play"} size={20} color="var(--on-brand)" />
                        </button>
                        <button className="icon-btn" data-testid="player-forward-10" onClick={() => seekTo(Math.min(status.duration || 0, status.currentTime + 10))}>
                          <Icon name="play-forward" size={18} />
                        </button>
                      </div>
                    </div>
                    <SeekBar position={status.currentTime} duration={status.duration || 0} onSeek={seekTo} testid="song-seekbar" loop={loop} />
                    <div className="t-hint">Deslize ou toque na barra para ir ao segundo que quer ensaiar.</div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="song-loop">
                      <button data-testid="loop-set-a" onClick={() => { setLoopA(status.currentTime); if (loopB !== null && loopB <= status.currentTime) setLoopB(null); }} className="chip" style={loopA !== null ? { borderColor: "var(--warning)", color: "var(--warning)" } : undefined}>
                        A {loopA !== null ? fmtClock(loopA) : "AQUI"}
                      </button>
                      <button data-testid="loop-set-b" onClick={() => { if (loopA === null || status.currentTime > loopA) setLoopB(status.currentTime); }} className="chip" style={loopB !== null ? { borderColor: "var(--warning)", color: "var(--warning)" } : undefined}>
                        B {loopB !== null ? fmtClock(loopB) : "AQUI"}
                      </button>
                      <button data-testid="loop-toggle" disabled={!loop} onClick={() => { if (looping) setLooping(false); else if (loop) { setLooping(true); seekTo(loop.a); play(); } }} className="chip" style={{ gap: 6, ...(looping ? { background: "var(--warning)", borderColor: "var(--warning)", color: "var(--on-warning)" } : {}), ...(!loop ? { opacity: 0.4 } : {}) }}>
                        <Icon name="repeat" size={14} color={looping ? "var(--on-warning)" : "var(--on-surface)"} /> {looping ? "A REPETIR" : "REPETIR A→B"}
                      </button>
                      {loopA !== null || loopB !== null ? (
                        <button className="icon-btn" data-testid="loop-clear" onClick={clearLoop} style={{ width: 36, height: 36 }}>
                          <Icon name="close" size={16} color="var(--muted)" />
                        </button>
                      ) : null}
                    </div>
                    <div className="t-hint" data-testid="song-loop-hint">
                      {looping && loop ? `A repetir ${fmtClock(loop.a)} → ${fmtClock(loop.b)} até dominar. Toque em "A repetir" para parar.` : 'Marque A e B para repetir um trecho em loop.'}
                    </div>
                    {current ? <MarkersPanel songId={id} voice={current} position={status.currentTime} onSeek={seekTo} onLoop={(a, b) => setLoopRange(a, b ?? status.duration)} /> : null}
                  </div>
                ) : null}
              </div>

              {/* RIGHT: tracks, scores, lyrics, progress */}
              <div style={isWide ? { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 24, paddingTop: 8 } : { display: "flex", flexDirection: "column", gap: 24 }} data-testid="song-text-column">
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div className="t-section">Outras faixas</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {others.map((p) => {
                      const active = current === p.voice;
                      return (
                        <button key={p.voice} data-testid={`part-play-${p.voice.toLowerCase()}`} onClick={() => playPart(p)} className="chip" style={{ height: 44, ...(active ? { borderColor: "var(--brand-primary)", background: "var(--brand-tertiary)" } : {}), ...(p.audio_url ? {} : { opacity: 0.4, cursor: "default" }) }}>
                          <Icon name={active && status.playing ? "pause" : "play"} size={16} /> {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="t-hint">Conteúdo exclusivo para membros · apenas para consulta na aplicação.</div>
                </div>

                {scores.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="song-scores">
                    <div className="t-section">Partitura</div>
                    <DocumentList documents={scores} />
                    <div className="t-hint">Consulte a partitura ou transfira o PDF para imprimir.</div>
                  </div>
                ) : null}

                {lyricsDocs.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="song-lyrics-pdfs">
                    <div className="t-section">Letra PDF</div>
                    <DocumentList documents={lyricsDocs} />
                  </div>
                ) : null}

                {otherDocs.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div className="t-section">Documentos</div>
                    <DocumentList documents={otherDocs} />
                  </div>
                ) : null}

                <Piano startNotes={song.start_notes || {}} myVoice={user?.voice} />

                <LyricsAssistant title={song.title} lyrics={song.lyrics} />

                <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }} data-testid="song-progress-card">
                  <div className="row-between">
                    <span className="t-section">O meu progresso</span>
                    <StatusPill status={song.status} data-testid="song-status" />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <ProgressBar value={song.progress} />
                    </div>
                    <span data-testid="song-progress-value" style={{ fontFamily: "var(--f-medium)", fontSize: 16, width: 48, textAlign: "right" }}>{song.progress ?? 0}%</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {STEPS.map((v) => (
                      <button key={v} data-testid={`progress-step-${v}`} onClick={() => progress.mutate(v)} className="chip" style={{ flex: 1, height: 40, justifyContent: "center", ...(song.progress === v ? { background: "var(--brand-primary)", borderColor: "var(--brand-primary)", color: "var(--on-brand)" } : {}) }}>
                        {v}%
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Chip label="LETRA" active={tab === "letra"} onClick={() => setTab("letra")} data-testid="song-tab-lyrics" />
                    <Chip label="NOTAS" active={tab === "notas"} onClick={() => setTab("notas")} data-testid="song-tab-notes" />
                  </div>
                  <div data-testid="song-text-content" style={{ color: "var(--on-surface-3)", fontSize: 16, lineHeight: "26px", whiteSpace: "pre-wrap" }}>
                    {(tab === "letra" ? song.lyrics : song.notes) || (tab === "letra" ? "Letra ainda não disponível." : "Sem notas.")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
